/**
 * Утверждение — то место, где человек исполняет FR-4.1: ни один пост не уходит в канал без
 * его явного одобрения. Всё в этом файле подчинено одному: человек обязан видеть, ЧТО
 * именно он подписывает, и подписать это не глядя должно быть невозможно.
 *
 * Порядок жёсткий (09 §6.1, §9.4): сначала слот, потом сводка «что будет подписано», и
 * только потом `POST …/approve`. Блок сводки не показывается, пока слот не выбран, а кнопка
 * удержания без слота не существует — то есть порядок не нарисован, а исполнен.
 *
 * `expected_content_hash` — `content_hash` модели рендера ДОСЛОВНО. Он защищает от
 * одобрения устаревшей версии: пока диалог открыт, версия могла быть заменена (переход 5),
 * и бэкенд ответит 409 `state_conflict` c `reason: 'content_changed'` вместо того, чтобы
 * выпустить ключ на текст, которого человек не видел.
 *
 * Расхождение с макетом Approve.html: там слоты, сводка и удержание нарисованы в одном
 * диалоге. Здесь так же — один диалог, — но сводка и кнопка удержания появляются после
 * выбора слота. Иначе «сначала SlotPicker, затем подтверждение» остаётся пожеланием.
 *
 * БРЕНД — ЧЕРНОВИКА, А НЕ АДРЕСА (2026-10-25, изоляция брендов). Режим, канал и слоты диалог
 * спрашивает для `model.brand`. Тело несёт то, что показано: `expected_brand` — бренд ЭКРАНА
 * (`screenBrand`, бренд адреса, в шапке и теме которого открыт диалог), `expected_chat_id` —
 * канал из строки «канал:» сводки. Бэкенд сверяет бренд экрана с брендом строки черновика, а
 * канал — с каналом, который подпишет, до записи и до демона подписи (`brand_mismatch` /
 * `channel_mismatch`). После перехода `DraftPanel` на адрес бренда черновика бренд экрана равен
 * `model.brand`; отправь диалог `model.brand`, сверка сравнивала бы строку черновика саму с
 * собой и не поймала бы экран, оставшийся без перехода. Канал не подтверждён — удержания нет:
 * назвать бэкенду нечего.
 */

import { getFirstUrl } from '@smm/contracts/preview';
import {
  type ApproveBody,
  BRAND_MISMATCH_REASON,
  CHANNEL_MISMATCH_REASON,
  posterDateLabel,
  posterRedatePrompt,
  postLabel,
  SIGNER_DRAFT_BRAND_PINNED_REASON,
} from '@smm/contracts/schemas';
import { type JSX, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import type {
  ApproveResponse,
  PublicationDispatch,
  RenderModel,
  RequestChangesResponse,
  ScheduleSlot,
} from '../../api/types.ts';
import {
  CHANNEL_BRAND_UNKNOWN_TEXT,
  CHANNEL_LOADING_TEXT,
  CHANNEL_UNKNOWN_TEXT,
  channelMissingText,
  readyChatId,
  useModeChannel,
} from '../../api/useModeChannel.ts';
import { useResource } from '../../api/useResource.ts';
import { fileSize } from '../../components/format.ts';
import { Modal } from '../../components/Modal.tsx';
import { cardAfterResave, SAVED_UNDER_POLICY } from '../../preview/LinkPreviewStrip.tsx';
import { plainMessagePhrase, SEND_MESSAGE_THRESHOLD } from '../../preview/validation.ts';
import { hasBlocking, type UiIssue } from '../contracts.ts';
import { posterSlotMismatch, visualSummary, visualViewOf } from '../visual/visualState.ts';
import { HoldToConfirm } from './HoldToConfirm.tsx';
import { signerPinnedText } from './phrases.ts';
import { SlotPicker, slotLabel } from './SlotPicker.tsx';

/** Сколько канонического текста показываем под подпись — 05-ui.md §6: «первые ~200 символов». */
const EXCERPT_CODEPOINTS = 200;

const RU_NUM = new Intl.NumberFormat('ru-RU');

/** `a41f7c02…9c2e` — хэш читается глазами только группами. */
export function shortHash(hash: string): string {
  return `${hash.slice(0, 4)} ${hash.slice(4, 8)} … ${hash.slice(-4)}`;
}

function excerptOf(text: string): { readonly text: string; readonly truncated: boolean } {
  // Режем по кодпоинтам, а не по code units: срез посреди суррогатной пары даёт
  // «крокозябру» ровно в блоке, который должен внушать доверие.
  const cps = [...text];
  return cps.length <= EXCERPT_CODEPOINTS
    ? { text, truncated: false }
    : { text: cps.slice(0, EXCERPT_CODEPOINTS).join(''), truncated: true };
}

/**
 * Строка карточки в сводке — тем же правилом, что полоса карточки превью (`getFirstUrl` по
 * подписанному). Политика бренда нужна одной ветке: версия подписана расширенной по политике,
 * а политику с тех пор выключили — «все посты уходят расширенным» было бы неправдой ровно там,
 * где человек подписывает пост (ревью, ui-10).
 */
function cardLine(canonical: RenderModel['canonical'], richAlways: boolean | undefined): string {
  const card = getFirstUrl(canonical);
  if (card.kind === 'card') return `карточка ссылки: будет — ${card.host}`;
  switch (card.reason) {
    case 'disabled':
      return 'карточка ссылки: не будет — отключена в этой версии';
    case 'rich_media':
      return 'карточка ссылки: не будет — пост с картинкой уходит расширенным сообщением, а параметра карточки у этого способа отправки нет';
    case 'rich_document':
      return 'карточка ссылки: не будет — к посту приложен файл, такой пост уходит расширенным сообщением, а параметра карточки у него нет';
    case 'rich_length':
      return `карточка ссылки: не будет — текст длиннее ${SEND_MESSAGE_THRESHOLD} кодпоинтов, такой пост уходит расширенным сообщением, а параметра карточки у него нет`;
    case 'rich_policy':
      if (richAlways === false) {
        return cardAfterResave(canonical).kind === 'card'
          ? `карточка ссылки: не будет — ${SAVED_UNDER_POLICY}; карточку вернёт новая версия`
          : `карточка ссылки: не будет — ${SAVED_UNDER_POLICY}`;
      }
      return 'карточка ссылки: не будет — все посты бренда уходят расширенным сообщением (Настройки), а параметра карточки у этого способа отправки нет';
    case 'no_url':
      return 'карточка ссылки: не будет — в тексте нет ссылки';
  }
}

export interface ApproveDialogProps {
  /** Бренд режима, канала и слотов диалога — `model.brand` (шапка файла). */
  readonly model: RenderModel;
  /**
   * Бренд ЭКРАНА (адреса) — ТОЛЬКО для `expected_brand`; режим, канал и слоты — `model.brand`.
   * Расхождение с брендом черновика значит, что переход `DraftPanel` не сработал, и бэкенд
   * отказывает `brand_mismatch`.
   */
  readonly screenBrand: string;
  readonly issues: readonly UiIssue[];
  /** Политика бренда «все посты — расширенным сообщением» СЕЙЧАС; `undefined` — неизвестна. */
  readonly richAlways?: boolean | undefined;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

export function ApproveDialog({
  model,
  screenBrand,
  issues,
  richAlways,
  onChanged,
  onClose,
}: ApproveDialogProps): JSX.Element {
  const brand = model.brand;
  const [slot, setSlot] = useState<{ slot: ScheduleSlot; timezone: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Расхождение, о котором сказал сам бэкенд (`poster_date_mismatch`): день слота он считает в
  // зоне бренда, и его ответ сильнее подсказки ниже, если они однажды разойдутся.
  const [serverMismatch, setServerMismatch] = useState<{
    posterDay: string;
    slotDay: string;
  } | null>(null);

  // ДАТА ОБЛОЖКИ — ДЕНЬ СЛОТА (docs/phase-2/10-posters.md). Обложка внутри подписи, поэтому
  // слот другого дня бэкенд отобьёт; экран говорит это ДО удержания кнопки и сразу предлагает
  // выход — перерисовать дату (переделка `redate:`, без вызова модели, секунды).
  const posterMismatch =
    serverMismatch ??
    (slot === null ? null : posterSlotMismatch(visualViewOf(model), slot.slot.slot_local));
  const [redating, setRedating] = useState(false);
  const redate = (): void => {
    if (posterMismatch === null || redating) return;
    setRedating(true);
    setError(null);
    post<RequestChangesResponse>(
      `/api/v1/drafts/${model.draft_id}/versions/${String(model.version_no)}/request-changes`,
      { prompt: posterRedatePrompt(posterMismatch.slotDay), targets: ['visual'] },
    )
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
        // 409 — на сервере уже не то, что показано (версию сменили, черновик ушёл дальше):
        // экран обновляется, как и после отказа одобрения, иначе человек жмёт по устаревшему.
        if (e instanceof ApiRequestError && e.status === 409) onChanged();
      })
      .finally(() => {
        setRedating(false);
      });
  };

  // Режим публикаций решает канал, и канал входит в подпись (`api/operations.ts`). Поэтому
  // диалог читает режим сам, называет канал в сводке и отправляет то, что показал: сменится
  // режим, пока диалог открыт, — бэкенд ответит `mode_changed`, а не подпишет пост на канал,
  // которого человек не видел. Режим неизвестен — утверждать нельзя: неизвестно, куда уйдёт пост.
  // Режим — бренда черновика (docs/multi-brand.md §3): у каждого бренда свой.
  const dispatch = useResource<PublicationDispatch>(
    `/api/v1/publication-dispatch?brand=${encodeURIComponent(brand)}`,
  );
  const mode = dispatch.data?.state;

  // Канал ТЕКУЩЕГО режима — и задан ли он вообще. Пустое поле режима — законное состояние
  // конфига (боевой канал заводится позже тестового), и бэкенд на него отказывает; экран
  // обязан сказать это ДО выбора слота и удержания кнопки, а не после. Канал входит в тело
  // (`expected_chat_id`), поэтому неподтверждённый канал закрывает удержание так же, как пустой.
  const { channel, refetch: refetchChannel } = useModeChannel(brand);
  const chatId = readyChatId(channel);
  const noChannel = chatId === null;

  const blocking = issues.filter((i) => i.level === 'blocking');
  const blocked = hasBlocking(issues);

  const canonical = model.canonical;
  const excerpt = excerptOf(canonical.text);
  const entityTypes = [...new Set(canonical.entities.map((e) => e.type))];
  const document = canonical.document ?? null;

  const submit = (): void => {
    if (slot === null || blocked || busy || mode === undefined || chatId === null) return;
    setBusy(true);
    setError(null);
    const body: ApproveBody = {
      slot_at: slot.slot.slot_at,
      expected_content_hash: model.content_hash,
      expected_mode: mode,
      expected_brand: screenBrand,
      expected_chat_id: chatId,
    };
    post<ApproveResponse>(
      `/api/v1/drafts/${model.draft_id}/versions/${model.version_no}/approve`,
      body,
    )
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e: unknown) => {
        if (e instanceof ApiRequestError && e.status === 409) {
          // 409 значит «на сервере уже не то, что показано». Экран обязан обновиться, иначе
          // человек будет жать кнопку по устаревшей картинке.
          const details = e.details;
          const reason =
            typeof details === 'object' && details !== null && 'reason' in details
              ? (details as { reason?: unknown }).reason
              : undefined;
          if (reason === 'poster_date_mismatch') {
            const d = details as { poster_date?: unknown; slot_date?: unknown };
            if (typeof d.poster_date === 'string' && typeof d.slot_date === 'string') {
              setServerMismatch({ posterDay: d.poster_date, slotDay: d.slot_date });
            }
          }
          setError(
            reason === 'poster_date_mismatch'
              ? `${e.message}. Ключ не выпущен.`
              : reason === 'content_changed'
                ? 'Черновик изменился с момента показа — обновите экран.'
                : reason === 'mode_changed'
                  ? 'Режим публикаций сменился, пока был открыт диалог: проверьте, в какой канал уйдёт пост, и утвердите заново.'
                  : reason === CHANNEL_MISMATCH_REASON
                    ? 'Канал режима сменился, пока был открыт диалог: ключ не выпущен. Проверьте канал в сводке и утвердите заново.'
                    : reason === SIGNER_DRAFT_BRAND_PINNED_REASON
                      ? signerPinnedText(e.message, 'Ключ не выпущен')
                      : reason === BRAND_MISMATCH_REASON
                        ? `${e.message}. Ключ не выпущен: пост принадлежит не тому бренду, что показан на экране. Обновите экран.`
                        : reason === 'channel_not_configured'
                          ? `${e.message}. Канал входит в подпись, поэтому ключ не выпущен и пост остался черновиком.`
                          : e.message,
          );
          dispatch.refetch();
          refetchChannel();
          onChanged();
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Утверждение"
      subtitle={`${postLabel(model.post_no)} · v${model.version_no}`}
      onClose={onClose}
    >
      {blocked ? (
        <div className="stack" style={{ gap: 8 }}>
          {blocking.map((i) => (
            <div key={i.code} className="val val-err">
              <span>{i.message}</span>
            </div>
          ))}
          <div className="micro">
            Пока блокирующая проверка не снята, утверждение недоступно: одобрять пост, который
            Telegram отвергнет, значит выпустить ключ впустую.
          </div>
        </div>
      ) : null}

      <SlotPicker
        brand={brand}
        selectedSlotId={slot === null ? null : slot.slot.slot_id}
        onPick={(picked, timezone) => {
          setSlot({ slot: picked, timezone });
          setServerMismatch(null);
        }}
      />

      {slot === null ? (
        <div className="micro">Выберите слот — после этого станет видно, что будет подписано.</div>
      ) : (
        <>
          <div
            className="stack"
            style={{
              gap: 10,
              background: 'var(--bg-alt)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-ctl)',
              padding: 14,
            }}
          >
            <span className="eyebrow" style={{ fontSize: 10 }}>
              Что будет подписано
            </span>
            <div className="small" style={{ color: 'var(--ink)', lineHeight: 1.5 }}>
              {excerpt.text}
              {excerpt.truncated ? '…' : ''}
            </div>
            <div className="stack" style={{ gap: 4 }}>
              <span className="meta">
                сущностей: {canonical.entities.length}
                {entityTypes.length === 0 ? '' : ` — ${entityTypes.join(', ')}`}
              </span>
              {/* Подписывается ЗАМОРОЖЕННОЕ содержание, и способ отправки в нём уже выбран:
                  `method` передаётся вместе с ним, а не выводится заново (шапка `first-url.ts`,
                  §9.3). */}
              <span className="meta">{cardLine(canonical, richAlways)}</span>
              <span className="meta">
                {canonical.media === null
                  ? `медиа: нет · способ отправки ${canonical.method} — ${canonical.method === 'sendMessage' ? plainMessagePhrase(canonical) : 'расширенное сообщение, карточки не будет'}`
                  : `медиа: ${canonical.media.mime_type} · ${RU_NUM.format(canonical.media.bytes)} байт · картинка первым блоком · способ отправки ${canonical.method} — расширенное сообщение, карточки не будет`}
              </span>
              {/* Файл — часть подписываемого (§9.2): его имя увидит читатель канала, байты
                  сверены хэшем. Строка есть только у поста с файлом: «файла нет» ничего не
                  подписывает и только удлиняло бы сводку. */}
              {document === null ? null : (
                <span className="meta">
                  {`файл: «${document.file_name}» · ${fileSize(document.bytes)} · PDF последним блоком · sha256 ${shortHash(document.sha256)}`}
                </span>
              )}
              {/* Строка `медиа: …` выше — про подписываемые байты; эта — про РЕШЕНИЕ человека
                  по картинке, и слова у неё те же, что в полосе визуала (§6.5). Диалог —
                  последнее место перед подписью, и след того, что человек на картинку
                  посмотрел, обязан быть виден именно здесь. */}
              <span className="meta">{visualSummary(visualViewOf(model))}</span>
              <span className="meta">
                sha256 {shortHash(model.content_hash)} · канон v{canonical.cv}
              </span>
              <span className="meta">
                слот: {slotLabel(slot.slot.slot_local)} ({slot.timezone})
              </span>
              {/* Chat_id — поле подписываемого payload (§Ключ 1.3), и здесь ему место по той
                  же причине, что и хэшу: человек видит то, на что ставится подпись. По нему
                  же видно подмену канала, которой слово «боевой» не покажет. */}
              <span className="meta">
                канал: {mode === undefined ? 'неизвестен' : mode === 'live' ? 'боевой' : 'тестовый'}
                {channel.kind === 'ready'
                  ? ` · ${channel.channelId}`
                  : channel.kind === 'missing'
                    ? ' · НЕ ЗАДАН'
                    : ''}
              </span>
            </div>
            <div className="micro">
              Подписывается каноническая форма — байт в байт то, что уйдёт в Telegram. Любая правка
              после утверждения потребует нового ключа.
            </div>
          </div>

          {channel.kind === 'missing' ? (
            <div className="val val-err" role="alert">
              <span>{channelMissingText(mode)}</span>
            </div>
          ) : channel.kind === 'unknown' ? (
            <div className="val val-err" role="alert">
              <span>{CHANNEL_BRAND_UNKNOWN_TEXT}</span>
            </div>
          ) : channel.kind === 'failed' || channel.kind === 'malformed' ? (
            <div className="val val-err" role="alert">
              <span>{CHANNEL_UNKNOWN_TEXT}</span>
            </div>
          ) : channel.kind === 'loading' ? (
            <div className="micro">{CHANNEL_LOADING_TEXT}</div>
          ) : null}

          {mode === 'live' ? (
            <div className="val val-warn">
              <span>Боевой режим: пост уйдёт в боевой канал — его увидят подписчики.</span>
            </div>
          ) : mode === 'test' ? (
            <div className="val val-info">
              <span>
                Тестовый режим: пост уйдёт в тестовый канал. В боевой он не попадёт и после
                переключения режима — канал входит в подпись.
              </span>
            </div>
          ) : dispatch.error === undefined ? (
            <div className="micro">Загружаем режим публикаций…</div>
          ) : (
            <div className="val val-err" role="alert">
              <span>
                Режим публикаций не загрузился — неизвестно, в какой канал уйдёт пост. Обновите
                экран.
              </span>
            </div>
          )}

          {posterMismatch === null ? null : (
            <div className="stack" style={{ gap: 8 }}>
              <div className="val val-warn" role="alert">
                <span>
                  {`На обложке ${posterDateLabel(posterMismatch.posterDay)}, а слот — ${posterDateLabel(posterMismatch.slotDay)}. `}
                  Обложка входит в подпись, поэтому день нужно перерисовать до утверждения: крючок и
                  фон останутся прежними, пост уйдёт на переделку на несколько секунд и вернётся
                  новой версией. Либо выберите слот того дня, что нарисован.
                </span>
              </div>
              <button
                type="button"
                className={redating ? 'btn btn-w btn-dis' : 'btn btn-ghost btn-w'}
                disabled={redating}
                onClick={redate}
              >
                {`Перерисовать дату на ${posterDateLabel(posterMismatch.slotDay)}`}
              </button>
            </div>
          )}

          {error === null ? null : (
            <div className="val val-err">
              <span>{error}</span>
            </div>
          )}

          <div className="stack" style={{ gap: 8 }}>
            <HoldToConfirm
              label={`утверждаю в слот ${slotLabel(slot.slot.slot_local)}`}
              disabled={
                blocked || busy || mode === undefined || noChannel || posterMismatch !== null
              }
              onConfirm={submit}
            />
            <div className="micro" style={{ textAlign: 'center' }}>
              удержание 800 мс · отпустите раньше — отмена
            </div>
            <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
              Отмена
            </button>
          </div>

          <div className="notice">
            Одобрение выпустит ключ публикации, действительный ровно до слота. Перенос слота — это
            новое утверждение и новый ключ: автоматических повторов в системе нет.
          </div>
        </>
      )}
    </Modal>
  );
}
